import { useContext, useEffect, useCallback, useState } from 'react';
import { FabricContext } from '@context/FabricContext';
import { useSelectedMVComment } from '@hooks';
import { useAssetViewerState } from '@hooks/useAssetViewerState';

export default function useHasAnnotations() {
  const selectedComment = useSelectedMVComment();
  const { selectedCommentId, canvasMode } = useAssetViewerState();
  const { canvas, getObjects } = useContext(FabricContext);
  const [has, setHas] = useState<boolean>(false);

  const isDraftComment =
    selectedCommentId === 'new' || selectedCommentId === 'reply';
  const useCanvasAsSource = isDraftComment || canvasMode === 'edit';

  const syncHasAnnotations = useCallback(() => {
    if (getObjects().length > 0) {
      setHas(true);
      return;
    }

    if (useCanvasAsSource) {
      setHas(false);
      return;
    }

    if (
      selectedComment?.annotations &&
      selectedComment.annotations.length > 0
    ) {
      setHas(true);
      return;
    }
    setHas(false);
  }, [getObjects, selectedComment, useCanvasAsSource]);

  useEffect(() => {
    syncHasAnnotations();
  }, [syncHasAnnotations]);

  useEffect(() => {
    if (!canvas) return;
    syncHasAnnotations();
    canvas.on('canvas:cleared', syncHasAnnotations);
    canvas.on('object:history:add', syncHasAnnotations);
    canvas.on('object:history:remove', syncHasAnnotations);
    return () => {
      canvas.off('canvas:cleared', syncHasAnnotations);
      canvas.off('object:history:remove', syncHasAnnotations);
      canvas.off('object:history:add', syncHasAnnotations);
    };
  }, [canvas, syncHasAnnotations]);

  return has;
}
